import type {
  SortDir,
  SortState,
  TableViewState,
} from '@lms/ui/use-table-view';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface UrlTableConfig {
  /** Valid sort keys. Anything else in the URL falls back to `defaultSort`. */
  sortKeys: string[];
  /** Sort applied when the URL carries none (or an invalid one). */
  defaultSort?: { key: string; dir: SortDir } | null;
  /** Per-filter valid option values and the default option. */
  filters: Record<string, { options: string[]; default: string }>;
  /** Namespaces the params so two tables can coexist. Default: none. */
  prefix?: string;
}

/**
 * Bridges `useTableView`'s controlled state to react-router search params so
 * sort/filter selections survive navigation (list → detail → back). Writes are
 * `replace` so they don't spam browser history, default values are omitted to
 * keep URLs tidy, and unknown/invalid param values fall back to defaults.
 */
export function useUrlTableState(config: UrlTableConfig): {
  value: TableViewState;
  onChange: (next: TableViewState) => void;
} {
  const { sortKeys, defaultSort = null, filters, prefix = '' } = config;
  const [searchParams, setSearchParams] = useSearchParams();

  const sortParam = `${prefix}sort`;
  const dirParam = `${prefix}dir`;
  const filterParam = useCallback(
    (key: string) => `${prefix}f_${key}`,
    [prefix],
  );

  const value = useMemo<TableViewState>(() => {
    const rawKey = searchParams.get(sortParam);
    const rawDir = searchParams.get(dirParam);
    const sort: SortState | null =
      rawKey && sortKeys.includes(rawKey)
        ? { key: rawKey, dir: rawDir === 'desc' ? 'desc' : 'asc' }
        : defaultSort;

    const resolved: Record<string, string> = {};
    for (const [key, cfg] of Object.entries(filters)) {
      const raw = searchParams.get(filterParam(key));
      if (raw && cfg.options.includes(raw)) resolved[key] = raw;
    }

    return { sort: sort ?? null, filters: resolved };
  }, [
    searchParams,
    sortParam,
    dirParam,
    filterParam,
    sortKeys,
    defaultSort,
    filters,
  ]);

  const onChange = useCallback(
    (next: TableViewState) => {
      const params = new URLSearchParams(searchParams);

      const sort = next.sort ?? null;
      const isDefaultSort =
        (!sort && !defaultSort) ||
        (sort &&
          defaultSort &&
          sort.key === defaultSort.key &&
          sort.dir === defaultSort.dir);
      if (!sort || isDefaultSort) {
        params.delete(sortParam);
        params.delete(dirParam);
      } else {
        params.set(sortParam, sort.key);
        params.set(dirParam, sort.dir);
      }

      for (const [key, cfg] of Object.entries(filters)) {
        const val = next.filters?.[key];
        if (!val || val === cfg.default || !cfg.options.includes(val)) {
          params.delete(filterParam(key));
        } else {
          params.set(filterParam(key), val);
        }
      }

      setSearchParams(params, { replace: true });
    },
    [
      searchParams,
      setSearchParams,
      sortParam,
      dirParam,
      filterParam,
      defaultSort,
      filters,
    ],
  );

  return { value, onChange };
}
